import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Style } from '@capacitor/status-bar';
import * as Sentry from '@sentry/capacitor';

import { useBusinessLayer } from '@mopla/business-logic';
import { SuccessStep } from '@mopla/constants';
import {
	Footer,
	MainWithFooterTemplate,
	useStatusBarStyleSync,
} from '@mopla/ui';
import { ContentWrapper } from '@mopla/ui/layouts/WelcomeScreen';
import { usePersistentCountdownGuard } from '@mopla/utils';

import { VerifyReminderScreen } from '../../../../organisms/VerifyReminderScreen/VerifyReminderScreen';
import { useWelcomePageStoreContext } from '../../useWelcomePageStore';
import { TSetInterceptor } from '../../utils/useInterceptor';

export interface IForgotPasswordFlowProps {
	setCloseInterceptor: TSetInterceptor;
	onBottomSliderClose: VoidFunction;
}

export const VerifyEmailFlow: FC<IForgotPasswordFlowProps> = ({
	setCloseInterceptor,
	onBottomSliderClose,
}) => {
	const { setSuccessType } = useWelcomePageStoreContext();
	const { push, pop } = useStatusBarStyleSync();
	const navigate = useNavigate();
	const { api } = useBusinessLayer();
	const { reinitCountdownGuard } = usePersistentCountdownGuard('emailSendTime');

	useEffect(() => {
		setCloseInterceptor(onBottomSliderClose);

		return () => {
			setCloseInterceptor(null);
		};
	}, [setCloseInterceptor, onBottomSliderClose]);

	useEffect(() => {
		push({
			title: 'ForgotPasswordFlow',
			style: Style.Light,
		});

		return () => {
			pop();
		};
	}, [push, pop]);

	const onResendEmailHandler = async () => {
		try {
			await api.post('/api/command/verifyEmail', { app: 'PASSENGER' });

			setSuccessType(SuccessStep.verifyEmail);
			reinitCountdownGuard();
			return navigate({ pathname: '/welcome', search: 'registration' });
		} catch (e) {
			Sentry.captureException(e);
		}
	};

	return (
		<VerifyReminderScreen
			onResendEmail={onResendEmailHandler}
			render={({ content, submit }) => (
				<MainWithFooterTemplate
					className="mainWithFooter"
					content={<ContentWrapper>{content}</ContentWrapper>}
					footer={<Footer>{submit}</Footer>}
				/>
			)}
		/>
	);
};
